import {hasArtifactVersionMetricName} from './artifactMetricData';
import {
  type ArtifactVersionMetricGridPanel,
  type ArtifactVersionsGrid,
  type ArtifactVersionsGridMetadata,
  createArtifactVersionMetricsPanel,
} from './artifactVersionsGridTypes';

export function isConfiguredArtifactVersionMetricPanel(
  panel: ArtifactVersionMetricGridPanel,
  membershipIds: string[]
): boolean {
  if (!hasConfiguredArtifactVersionMetricPanelConfig(panel)) {
    return false;
  }
  if (membershipIds.length === 0) {
    return false;
  }
  return true;
}

export function hasConfiguredArtifactVersionMetricPanelConfig(
  panel: ArtifactVersionMetricGridPanel
): boolean {
  if (panel.config.chartType == null) {
    return false;
  }
  if (!hasArtifactVersionMetricName(panel.config.metricName)) {
    return false;
  }
  if (
    panel.config.chartType === 'scatter' &&
    !hasArtifactVersionMetricName(panel.config.xMetricName)
  ) {
    return false;
  }
  return true;
}

export function getArtifactVersionMetricPanelsWithConfig(
  panels: ArtifactVersionMetricGridPanel[]
): ArtifactVersionMetricGridPanel[] {
  return panels.filter(hasConfiguredArtifactVersionMetricPanelConfig);
}

export function getConfiguredArtifactVersionMetricPanels(
  metadata: ArtifactVersionsGridMetadata
): ArtifactVersionMetricGridPanel[] {
  return getConfiguredArtifactVersionMetricPanelsForMembershipIds(
    metadata.panels,
    metadata.membershipIds
  );
}

export function getConfiguredArtifactVersionMetricPanelsForMembershipIds(
  panels: ArtifactVersionMetricGridPanel[],
  membershipIds: string[]
): ArtifactVersionMetricGridPanel[] {
  return panels.filter(panel =>
    isConfiguredArtifactVersionMetricPanel(panel, membershipIds)
  );
}

export function hasConfiguredArtifactVersionMetricPanel(
  metadata: ArtifactVersionsGridMetadata
): boolean {
  return getConfiguredArtifactVersionMetricPanels(metadata).length > 0;
}

export function getConfiguredArtifactVersionMetricNames(
  panels: ArtifactVersionMetricGridPanel[]
): string[] {
  const metricNames = new Set<string>();

  panels.forEach(panel => {
    if (panel.config.chartType == null) {
      return;
    }

    if (hasArtifactVersionMetricName(panel.config.metricName)) {
      metricNames.add(panel.config.metricName);
    }

    if (
      panel.config.chartType === 'scatter' &&
      hasArtifactVersionMetricName(panel.config.xMetricName)
    ) {
      metricNames.add(panel.config.xMetricName);
    }
  });

  return Array.from(metricNames);
}

export function ensureEditableArtifactVersionMetricPanel(
  metadata: ArtifactVersionsGridMetadata
): ArtifactVersionsGridMetadata {
  if (metadata.panels.length > 0) {
    return metadata;
  }

  return {
    ...metadata,
    panels: [createArtifactVersionMetricsPanel()],
  };
}

export function isEmptyArtifactVersionsGrid(
  element: ArtifactVersionsGrid
): boolean {
  return !hasConfiguredArtifactVersionMetricPanel(element.metadata);
}
